import {
  $createHeadingNode,
  $isHeadingNode,
  HeadingTagType,
} from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { Components } from "@thisgorilla/primate-ds";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
} from "lexical";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { ArticleContext } from "../../..";
import { useClickOutside } from "../../../../../../hooks/useClickOutside";
import { DROPDOWN_OVER_MODAL_Z_INDEX } from "../../../../../../utils";
import { $findTopLevelElement, styleType } from "../utils";

const StyleDropdown = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeStyle, setActiveStyle] = useState("");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const { editors, focusedEditor } = useContext(ArticleContext);
  const editor = editors[focusedEditor];
  useClickOutside({ ref: containerRef, setState: setDropdownOpen });

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = $findTopLevelElement(anchorNode);
      const type = $isHeadingNode(element) ? element.getTag() : "paragraph";
      setActiveStyle(type);
    }
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  return (
    <RelativeContainer $isActive={isDropdownOpen}>
      <Components.Button
        size="Medium"
        kind="Secondary"
        color="Neutral"
        disabled={false}
        label={
          activeStyle
            ? styleType[activeStyle as keyof typeof styleType]
            : "Style"
        }
        onClick={() => {
          setDropdownOpen(!isDropdownOpen);
        }}
        iconPosition="right"
        iconType="ChevronDown"
      />
      {isDropdownOpen && (
        <Container ref={containerRef}>
          <Components.Menu size="small" itemGap={0}>
            <Components.ListItem type="Label" label="Style" />
            {(
              Object.keys(styleType).slice(0, 3) as (keyof typeof styleType)[]
            ).map((item, index) => (
              <Components.ListItem
                type="List Item"
                label={styleType[item]}
                key={index}
                selected={activeStyle === item}
                onClick={() => {
                  setDropdownOpen(!isDropdownOpen);
                  if (!editor) return;
                  editor.focus();
                  editor.update(() => {
                    const selection = $getSelection();
                    $setBlocksType(selection, () => {
                      if (item === "paragraph") {
                        return $createParagraphNode();
                      }
                      return $createHeadingNode(item as HeadingTagType);
                    });
                  });
                }}
              />
            ))}
          </Components.Menu>
        </Container>
      )}
    </RelativeContainer>
  );
};

export default StyleDropdown;

const RelativeContainer = styled.div<{ $isActive: boolean }>`
  position: relative;
  & .container > button {
    background-color: ${({ $isActive, theme }) =>
      $isActive &&
      theme.Color.background.brand.subtler["default-active"].value};
    border-color: ${({ $isActive, theme }) =>
      $isActive && theme.Color.border["ui-border-emphasize-brand"].value};
  }
`;

const Container = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing["spacing-13"].value}rem;
  left: 0;
  z-index: ${DROPDOWN_OVER_MODAL_Z_INDEX};
  border-radius: ${({ theme }) => theme.radius["radius-xs"].value}rem;
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-primary"].value};
`;
